import type { CollectionData, ViewDef } from '@tomo/core';
import { clampRect, IconButton, isEditableTarget, isMaxSnap, SNAPS, snapRect, toast, Window, WindowPreview, type DeskSize, type WindowSnap } from '@tomo/ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Component, memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useCollection, useRecord } from '../../data/hooks.tsx';
import { useHotkey } from '../../lib/hotkeys.ts';
import { Icon } from '../../lib/icons.tsx';
import { useViewRecords } from '../collections/view-utils.ts';
import { RecordView } from '../records/RecordView.tsx';
import { useWindowManager, useWindows } from './hooks.ts';
import { isCompactDesk, type RecordWindow } from './windows.ts';
import './windows.css';

/** The layer over the page where records open in windows. */
export function WindowDesk() {
  const manager = useWindowManager();
  const { wins, order, active, focus, desk } = useWindows();
  const ref = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<WindowSnap | null>(null);
  const compact = isCompactDesk(desk);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => manager.setDesk({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [manager]);

  // Clicking or tabbing into the page hands the keyboard back to it.
  useEffect(() => {
    const onPage = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.app__content')) manager.deactivate();
    };
    document.addEventListener('pointerdown', onPage, true);
    document.addEventListener('focusin', onPage, true);
    return () => {
      document.removeEventListener('pointerdown', onPage, true);
      document.removeEventListener('focusin', onPage, true);
    };
  }, [manager]);

  return (
    <div ref={ref} className="desk" data-compact={compact || undefined}>
      {desk.width > 0 && (
        <>
          {/* Level with the window in front, and before it in the DOM: under it, over the rest. */}
          {preview && <WindowPreview rect={snapRect(preview, desk)} zIndex={order.length} />}
          {wins.map((win) => (
            <WindowBoundary key={win.id} onError={() => manager.close(win.id)}>
              <RecordWindowFrame
                win={win}
                zIndex={order.indexOf(win.id) + 1}
                active={active === win.id}
                desk={desk}
                compact={compact}
                focusN={focus?.id === win.id ? focus.n : 0}
                onSnapPreview={setPreview}
              />
            </WindowBoundary>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * A window that fails to render closes instead of taking the page down with it (its state is
 * saved, so it would fail again after every reload).
 */
class WindowBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    console.error(error);
    toast.error('A window couldn’t be shown, so it was closed.');
    this.props.onError();
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

interface FrameProps {
  win: RecordWindow;
  zIndex: number;
  active: boolean;
  desk: DeskSize;
  compact: boolean;
  /** Changes when the manager asks for focus to move into this window. */
  focusN: number;
  onSnapPreview: (snap: WindowSnap | null) => void;
}

const RecordWindowFrame = memo(function RecordWindowFrame({ win, zIndex, active, desk, compact, focusN, onSnapPreview }: FrameProps) {
  const manager = useWindowManager();
  const record = useRecord(win.recordId);
  const collection = useCollection(record?.collectionId);
  const scroller = useRef<HTMLDivElement>(null);
  const title = record?.title || 'Untitled';
  const maximized = compact || isMaxSnap(win.snap);
  const rect = compact ? snapRect(SNAPS.max, desk, 0) : win.snap ? snapRect(win.snap, desk) : clampRect(win.rect, desk);

  // Deleted for good (it was here, then it's gone): the window goes too.
  const seen = useRef(false);
  useEffect(() => {
    if (record) seen.current = true;
    else if (seen.current) manager.close(win.id);
  }, [record, manager, win.id]);

  // Opened, or picked in the taskbar: focus moves in, unless it's already inside (a new title).
  useEffect(() => {
    const el = scroller.current;
    if (focusN && el && !el.closest('.g-window')?.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [focusN]);

  // Another record in the same window (previous/next) starts at the top.
  useEffect(() => {
    scroller.current?.scrollTo(0, 0);
  }, [win.recordId]);

  // Escape leaves a field or the editor first; the next one closes the window.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.nativeEvent.isComposing) return;
    e.stopPropagation();
    if (isEditableTarget(e.target)) scroller.current?.focus();
    else manager.close(win.id);
  };

  // Moving or stacking the window doesn't re-render the record.
  const content = useMemo(
    () => (
      <div ref={scroller} className="record-win__scroll" tabIndex={-1}>
        <RecordView recordId={win.recordId} mode="window" />
      </div>
    ),
    [win.recordId],
  );

  return (
    <Window
      className="record-win"
      rect={rect}
      restoreSize={win.rect}
      snapped={!!win.snap && !maximized}
      maximized={maximized}
      desk={desk}
      active={active}
      zIndex={zIndex}
      movable={!compact}
      hidden={win.minimized}
      label={title}
      icon={<Icon token={record?.icon ?? collection?.icon} size={15} />}
      title={
        <>
          <span className="g-truncate">{title}</span>
          {collection && <span className="record-win__where g-truncate">{collection.name}</span>}
        </>
      }
      actions={win.from && <Siblings win={win} active={active} />}
      onActivate={() => manager.activate(win.id)}
      onClose={() => manager.close(win.id)}
      onMinimize={() => manager.minimize(win.id)}
      onToggleMaximize={() => manager.toggleMaximize(win.id)}
      onPlace={(place) => manager.place(win.id, place)}
      onSnapPreview={onSnapPreview}
      onKeyDown={onKeyDown}
    >
      {content}
    </Window>
  );
});

/** Previous and next through the collection view the window was opened from. */
function Siblings({ win, active }: { win: RecordWindow; active: boolean }) {
  const collection = useCollection(win.from?.collectionId);
  const view = collection?.views.find((v) => v.id === win.from?.viewId) ?? collection?.views[0];
  if (!collection || !view) return null;
  return <SiblingNav win={win} active={active} collection={collection} view={view} />;
}

function SiblingNav({ win, active, collection, view }: { win: RecordWindow; active: boolean; collection: CollectionData; view: ViewDef }) {
  const manager = useWindowManager();
  const records = useViewRecords(collection, view, '');
  const index = records.findIndex((r) => r.id === win.recordId);
  const prev = index > 0 ? records[index - 1] : undefined;
  const next = index >= 0 ? records[index + 1] : undefined;
  const enabled = active && !win.minimized;
  useHotkey('alt+up', () => prev && manager.show(win.id, prev.id), { allowInEditable: true, enabled });
  useHotkey('alt+down', () => next && manager.show(win.id, next.id), { allowInEditable: true, enabled });
  if (index < 0) return null;
  return (
    <>
      <IconButton label="Previous" size="sm" disabled={!prev} onClick={() => prev && manager.show(win.id, prev.id)} tooltipShortcut="alt+up">
        <ChevronUp />
      </IconButton>
      <IconButton label="Next" size="sm" disabled={!next} onClick={() => next && manager.show(win.id, next.id)} tooltipShortcut="alt+down">
        <ChevronDown />
      </IconButton>
      <span className="record-win__pos tabular">
        {index + 1} / {records.length}
      </span>
    </>
  );
}
